/**
 * Babylon engine and scene construction.
 *
 * All geometry is built from primitives at runtime — there are no downloaded
 * models. That keeps the project legally clean (nothing here belongs to anyone
 * else) and means a real artist can replace a shape later without the rest of
 * the code caring, because everything is driven by the zone data.
 */

import { Engine } from '@babylonjs/core/Engines/engine.js';
import { WebGPUEngine } from '@babylonjs/core/Engines/webgpuEngine.js';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine.js';
import { Scene } from '@babylonjs/core/scene.js';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color.js';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight.js';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight.js';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration.js';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture.js';
import { ColorCurves } from '@babylonjs/core/Materials/colorCurves.js';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline.js';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder.js';
import { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera.js';
import '@babylonjs/core/Meshes/thinInstanceMesh.js';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent.js';
import '@babylonjs/core/Rendering/depthRendererSceneComponent.js';

import type { AdSlotDef, BuildingDef, PropDef, Surface, ZoneDef } from '@emberreach/shared';
import { groundHeightAt } from '@emberreach/shared';
import type { QualitySettings } from './quality.js';
import { surfaceTexture } from './textures.js';

// --- palette ---------------------------------------------------------------

const SURFACE_COLOURS: Record<Surface, string> = {
  stone: '#8c8378',
  darkStone: '#4a4640',
  timber: '#8a6440',
  darkTimber: '#5a4230',
  plaster: '#d6c8ac',
  thatch: '#b39a5e',
  tile: '#a2523c',
  slate: '#4e565c',
  metal: '#6e7276',
  ember: '#e2622a',
  water: '#38646e',
  grass: '#5f9e42',
  ash: '#6b5f57',
  sand: '#c2ab86',
  road: '#7a7068'
};

export interface EngineBundle {
  engine: AbstractEngine;
  scene: Scene;
  camera: UniversalCamera;
  /** True when WebGPU was available; false means the WebGL2 fallback is in use. */
  webgpu: boolean;
  shadows: ShadowGenerator | null;
  sun: DirectionalLight;
}

/**
 * Creates the engine, preferring WebGPU and falling back to WebGL2.
 *
 * The fallback is not optional politeness: WebGPU is still absent or disabled on
 * a large share of phones, and this is where "works on phones" is won or lost.
 */
export async function createEngine(
  canvas: HTMLCanvasElement,
  quality: QualitySettings
): Promise<{ engine: AbstractEngine; webgpu: boolean }> {
  if (await WebGPUEngine.IsSupportedAsync) {
    try {
      const engine = new WebGPUEngine(canvas, {
        antialias: quality.antialias,
        stencil: true
      });
      await engine.initAsync();
      return { engine, webgpu: true };
    } catch {
      // Supported but failed to initialise — a driver problem, not a reason to
      // give up. Fall through to WebGL2.
    }
  }

  const engine = new Engine(canvas, quality.antialias, {
    stencil: true,
    preserveDrawingBuffer: false,
    // Battery on a phone matters more than a couple of frames.
    powerPreference: 'high-performance',
    failIfMajorPerformanceCaveat: false
  });
  return { engine, webgpu: false };
}

/**
 * @param repeat how many times the surface texture tiles across a mesh. Two is
 *   right for a wall; the ground needs far more or the grass comes out the size
 *   of a house. Part of the cache key, so both can exist at once.
 */
function material(
  scene: Scene,
  name: string,
  surface: Surface,
  quality: QualitySettings,
  repeat = 2,
  /** Multiplies the palette colour. For a shade of an existing surface that
   *  does not deserve a surface of its own — pine against meadow grass. */
  tint = 1
): StandardMaterial {
  const existing = scene.getMaterialByName(name);
  if (existing instanceof StandardMaterial) return existing;

  const m = new StandardMaterial(name, scene);
  // Falls back rather than throwing: an unpainted wall is a far better outcome
  // than a zone that refuses to build because somebody added a surface name.
  m.diffuseColor = Color3.FromHexString(SURFACE_COLOURS[surface] ?? '#8c8378').scale(tint);
  m.specularColor = new Color3(0.05, 0.05, 0.05);

  /* The pattern is drawn in greyscale and multiplied by the colour above, so a
     surface's colour stays defined in exactly one place — this palette — and
     the texture only supplies the relief. */
  const texture = surfaceTexture(scene, surface, repeat);
  if (texture !== null) m.diffuseTexture = texture;

  if (surface === 'ember') {
    m.emissiveColor = Color3.FromHexString('#8a3410');
  }
  if (surface === 'water') {
    m.alpha = 0.82;
    m.specularColor = new Color3(0.4, 0.45, 0.45);
  }
  // Freezing tells Babylon the material will not change, which skips a
  // per-frame check. With this many meshes it is worth having.
  if (!quality.particles) m.freeze();
  return m;
}

export function createScene(
  engine: AbstractEngine,
  zone: ZoneDef,
  quality: QualitySettings
): { scene: Scene; camera: UniversalCamera; shadows: ShadowGenerator | null; sun: DirectionalLight } {
  const scene = new Scene(engine);
  scene.clearColor = Color4.FromHexString(zone.ambience.skyBottom + 'ff');
  scene.ambientColor = Color3.FromHexString(zone.ambience.ambientColour);

  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogColor = Color3.FromHexString(zone.ambience.fogColour);
  scene.fogDensity = zone.ambience.fogDensity;

  // Filmic (ACES) tone mapping + exposure. The single biggest step away from the
  // flat, washed-out primitive look toward a natural, photographic response.
  scene.imageProcessingConfiguration.toneMappingEnabled = true;
  scene.imageProcessingConfiguration.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  scene.imageProcessingConfiguration.exposure = 1.28;
  scene.imageProcessingConfiguration.contrast = 1.3;
  const curves = new ColorCurves();
  curves.globalSaturation = 58;
  scene.imageProcessingConfiguration.colorCurvesEnabled = true;
  scene.imageProcessingConfiguration.colorCurves = curves;

  // Not a player camera — the third-person controller drives it every frame.
  const camera = new UniversalCamera('camera', new Vector3(0, 3, -8), scene);
  camera.minZ = 0.25;
  camera.maxZ = 520;
  camera.fov = 0.9;

  const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  /* Lifted from 0.55. With flat-shaded primitives and a bright sky, the old
     value left everything at ground level reading almost black against the
     horizon — the ground was the same green as the grass and looked like tar. */
  hemi.intensity = 0.95;
  hemi.diffuse = Color3.FromHexString(zone.ambience.ambientColour).scale(1.3);
  hemi.groundColor = Color3.FromHexString(zone.ambience.fogColour);

  const sun = new DirectionalLight(
    'sun',
    new Vector3(...zone.ambience.sunDirection),
    scene
  );
  sun.intensity = 1.65;
  sun.diffuse = Color3.FromHexString(zone.ambience.sunColour);
  sun.position = new Vector3(0, 90, 0);

  let shadows: ShadowGenerator | null = null;
  if (quality.shadows && quality.shadowMapSize > 0) {
    shadows = new ShadowGenerator(quality.shadowMapSize, sun);
    shadows.useExponentialShadowMap = true;
    /* 0.65, not 0.42. The old value put the whole side of the square in near
       darkness whenever a tall building stood between it and the sun, and since
       shadow is the only light variation a flat-shaded scene has, the town read
       as gloomy rather than shaded. */
    shadows.darkness = 0.42;
  }

  // Gradient sky dome — real atmosphere instead of a flat clear colour, so the horizon
  // reads and the world has open air above it. Follows the camera, ignores lighting + fog.
  const sky = MeshBuilder.CreateSphere('skydome', { diameter: 900, segments: 18, sideOrientation: Mesh.BACKSIDE }, scene);
  sky.infiniteDistance = true; sky.applyFog = false; sky.isPickable = false;
  const skyTex = new DynamicTexture('skytex', { width: 16, height: 256 }, scene, false);
  const sctx = skyTex.getContext() as unknown as CanvasRenderingContext2D;
  const sgrad = sctx.createLinearGradient(0, 0, 0, 256);
  sgrad.addColorStop(0, zone.ambience.skyTop);
  sgrad.addColorStop(0.55, zone.ambience.skyBottom);
  sgrad.addColorStop(1, zone.ambience.fogColour);
  sctx.fillStyle = sgrad; sctx.fillRect(0, 0, 16, 256); skyTex.update();
  const skyMat = new StandardMaterial('skymat', scene);
  skyMat.backFaceCulling = false; skyMat.disableLighting = true;
  skyMat.emissiveTexture = skyTex; skyMat.diffuseColor = new Color3(0, 0, 0); skyMat.specularColor = new Color3(0, 0, 0);
  sky.material = skyMat;

  // Cinematic post-processing. Bloom makes emissive things — embers, forge glow, lamps,
  // lit windows, the sun's hot highlights — actually bloom, and a light sharpen + vignette +
  // fine grain lift it from flat primitives toward something that reads as lit and filmic.
  const pipe = new DefaultRenderingPipeline('cine', true, scene, [camera]);
  pipe.bloomEnabled = true;
  pipe.bloomThreshold = 0.92;
  pipe.bloomWeight = 0.55;
  pipe.bloomKernel = 64;
  pipe.bloomScale = 0.6;
  pipe.sharpenEnabled = true;
  pipe.sharpen.edgeAmount = 0.28;
  pipe.grainEnabled = true;
  pipe.grain.intensity = 5;
  pipe.grain.animated = true;
  scene.imageProcessingConfiguration.vignetteEnabled = true;
  scene.imageProcessingConfiguration.vignetteWeight = 2.6;
  scene.imageProcessingConfiguration.vignetteColor = new Color4(0, 0, 0, 0);

  return { scene, camera, shadows, sun };
}

// --- geometry --------------------------------------------------------------

function addRoof(
  scene: Scene,
  parent: TransformNode,
  def: BuildingDef,
  quality: QualitySettings
): void {
  if (def.roof === 'none') return;

  const surface = def.roofSurface ?? 'slate';
  const mat = material(scene, `mat-${surface}`, surface, quality);
  const [w, d] = def.size;

  if (def.roof === 'flat') {
    const slab = MeshBuilder.CreateBox(`${def.id}-roof`, { width: w + 0.6, depth: d + 0.6, height: 0.4 }, scene);
    slab.position.y = def.height + 0.2;
    slab.material = mat;
    slab.parent = parent;
    return;
  }

  if (def.roof === 'gable') {
    // A triangular prism: a rotated box reads as a pitched roof from any angle
    // a player will actually see it from, at a fraction of the vertices.
    const pitch = Math.min(w, d) * 0.42;
    const left = MeshBuilder.CreateBox(`${def.id}-roof-l`, { width: w * 0.62, depth: d + 0.8, height: 0.35 }, scene);
    left.rotation.z = Math.PI / 5;
    left.position.set(-w * 0.24, def.height + pitch * 0.5, 0);
    left.material = mat;
    left.parent = parent;

    const right = MeshBuilder.CreateBox(`${def.id}-roof-r`, { width: w * 0.62, depth: d + 0.8, height: 0.35 }, scene);
    right.rotation.z = -Math.PI / 5;
    right.position.set(w * 0.24, def.height + pitch * 0.5, 0);
    right.material = mat;
    right.parent = parent;
    return;
  }

  // hip: a squat pyramid
  const pyramid = MeshBuilder.CreateCylinder(
    `${def.id}-roof`,
    { diameterTop: 0, diameterBottom: Math.max(w, d) * 1.12, height: Math.min(w, d) * 0.42, tessellation: 4 },
    scene
  );
  pyramid.rotation.y = Math.PI / 4;
  pyramid.position.y = def.height + Math.min(w, d) * 0.21;
  pyramid.material = mat;
  pyramid.parent = parent;
}

/**
 * Windows, on the two long walls.
 *
 * They are cut in whether or not the building is lit. An unlit building with no
 * openings at all is a shipping container; the dark recess is what says people
 * live here and have simply gone to bed.
 */
function addWindows(
  scene: Scene,
  parent: TransformNode,
  def: BuildingDef,
  quality: QualitySettings
): void {
  const paneMat = new StandardMaterial(`${def.id}-pane`, scene);
  if (def.lit === true) {
    paneMat.diffuseColor = Color3.FromHexString('#3a2a18');
    paneMat.emissiveColor = Color3.FromHexString('#ffb45c');
  } else {
    // Not black: a window with nothing behind it still catches a little sky,
    // and a pure black hole in a wall reads as a missing polygon.
    paneMat.diffuseColor = Color3.FromHexString('#1d2126');
    paneMat.emissiveColor = Color3.FromHexString('#0b0e11');
  }
  if (!quality.particles) paneMat.freeze();

  const frameMat = material(scene, 'mat-darkTimber', 'darkTimber', quality);
  const [w, d] = def.size;
  const rows = def.height > 7 ? 2 : 1;
  const perSide = Math.max(1, Math.min(4, Math.floor(w / 3.2)));

  for (let row = 0; row < rows; row++) {
    const y = 2.1 + row * 3.1;
    if (y > def.height - 1.2) continue;
    for (let i = 0; i < perSide; i++) {
      const offset = (i - (perSide - 1) / 2) * (w / (perSide + 0.4));
      for (const side of [1, -1]) {
        // The surround sits proud of the wall, so the opening has a shadow line
        // down one edge instead of being a decal painted on a flat face.
        const surround = MeshBuilder.CreateBox(
          `${def.id}-winframe-${row}-${i}-${side}`,
          { width: 1.34, height: 1.54, depth: 0.14 },
          scene
        );
        surround.position.set(offset, y, (side * d) / 2 + side * 0.04);
        surround.material = frameMat;
        surround.isPickable = false;
        surround.parent = parent;

        const pane = MeshBuilder.CreatePlane(
          `${def.id}-win-${row}-${i}-${side}`, { width: 1.1, height: 1.3 }, scene
        );
        pane.position.set(offset, y, (side * d) / 2 + side * 0.13);
        pane.rotation.y = side === 1 ? 0 : Math.PI;
        pane.material = paneMat;
        pane.isPickable = false;
        pane.parent = parent;

        // A single glazing bar. One line across the glass is the difference
        // between a window and a lit rectangle.
        const bar = MeshBuilder.CreateBox(
          `${def.id}-winbar-${row}-${i}-${side}`,
          { width: 0.06, height: 1.3, depth: 0.03 }, scene
        );
        bar.position.set(offset, y, (side * d) / 2 + side * 0.15);
        bar.material = frameMat;
        bar.isPickable = false;
        bar.parent = parent;
      }
    }
  }
}

/**
 * A door, on the face pointing towards the middle of the settlement.
 *
 * Which wall the door goes on is not decoration: a building with its door round
 * the back looks like a mistake, and both towns are laid out around a centre, so
 * "the way the road is" and "towards the origin" are the same direction.
 * Snapped to the nearest quarter turn *after* the building's own rotation is
 * taken off, so a rotated building still gets a door on a real face.
 */
function addDoor(
  scene: Scene,
  parent: TransformNode,
  def: BuildingDef,
  quality: QualitySettings
): void {
  const [w, d] = def.size;

  const towardsCentre = Math.atan2(-def.position[0], -def.position[1]);
  const quarter = Math.PI / 2;
  const face = Math.round((towardsCentre - (def.rotation ?? 0)) / quarter) * quarter;

  // A door on the front or back wall stands off by half the depth; on an end
  // wall, by half the width.
  const onEndWall = Math.abs(Math.round(face / quarter)) % 2 === 1;
  const standoff = (onEndWall ? w : d) / 2;

  const hinge = new TransformNode(`${def.id}-doorway`, scene);
  hinge.rotation.y = face;
  hinge.parent = parent;

  const timber = material(scene, 'mat-darkTimber', 'darkTimber', quality);
  const height = Math.min(2.3, def.height - 0.4);

  const frame = MeshBuilder.CreateBox(
    `${def.id}-doorframe`, { width: 1.5, height: height + 0.22, depth: 0.16 }, scene
  );
  frame.position.set(0, (height + 0.22) / 2, standoff + 0.05);
  frame.material = timber;
  frame.isPickable = false;
  frame.parent = hinge;

  const leaf = MeshBuilder.CreateBox(
    `${def.id}-door`, { width: 1.16, height, depth: 0.1 }, scene
  );
  leaf.position.set(0, height / 2, standoff + 0.14);
  leaf.material = material(scene, 'mat-timber', 'timber', quality);
  leaf.isPickable = false;
  leaf.parent = hinge;

  // Handle. Two pixels of brass, and the door stops being a plank.
  const handle = MeshBuilder.CreateSphere(`${def.id}-handle`, { diameter: 0.13, segments: 6 }, scene);
  handle.position.set(0.42, height * 0.5, standoff + 0.2);
  const brass = material(scene, 'mat-metal', 'metal', quality);
  handle.material = brass;
  handle.isPickable = false;
  handle.parent = hinge;

  // A step, so the door meets the ground instead of floating on it.
  const step = MeshBuilder.CreateBox(
    `${def.id}-step`, { width: 1.9, height: 0.16, depth: 0.7 }, scene
  );
  step.position.set(0, 0.08, standoff + 0.42);
  step.material = material(scene, 'mat-stone', 'stone', quality);
  step.isPickable = false;
  step.parent = hinge;
}

export function buildBuilding(
  scene: Scene,
  def: BuildingDef,
  groundY: number,
  quality: QualitySettings,
  shadows: ShadowGenerator | null
): TransformNode {
  const root = new TransformNode(`building-${def.id}`, scene);
  root.position.set(def.position[0], groundY, def.position[1]);
  root.rotation.y = def.rotation ?? 0;

  const [w, d] = def.size;

  const body = MeshBuilder.CreateBox(
    `${def.id}-body`,
    { width: w, depth: d, height: def.height },
    scene
  );
  body.position.y = def.height / 2;
  body.material = material(scene, `mat-${def.walls}`, def.walls, quality);
  body.parent = root;
  body.receiveShadows = true;
  // Buildings are big enough that the camera should never see through them.
  body.metadata = { cameraBlocker: true };
  shadows?.addShadowCaster(body);

  const timber = material(scene, 'mat-darkTimber', 'darkTimber', quality);

  /* A stone plinth. Every building previously met the ground on a hard line,
     which is the single clearest tell of an untouched primitive — real walls sit
     on something, and the ground is never quite flat. */
  const plinth = MeshBuilder.CreateBox(
    `${def.id}-plinth`, { width: w + 0.34, depth: d + 0.34, height: 0.5 }, scene
  );
  plinth.position.y = 0.25;
  // Stone rather than dark stone: at ground level, in the shade of the wall
  // above it, the dark version read as a black gap under the building.
  plinth.material = material(scene, 'mat-stone', 'stone', quality);
  plinth.receiveShadows = true;
  plinth.isPickable = false;
  plinth.parent = root;

  /* Corner posts. Four thin uprights that pick out the edges of the box —
     without them a flat-lit wall meeting another flat-lit wall at 90° has no
     visible corner at all from most angles. */
  for (const cx of [-1, 1]) {
    for (const cz of [-1, 1]) {
      const post = MeshBuilder.CreateBox(
        `${def.id}-post-${cx}-${cz}`,
        { width: 0.26, depth: 0.26, height: def.height },
        scene
      );
      post.position.set(cx * (w / 2 - 0.06), def.height / 2, cz * (d / 2 - 0.06));
      post.material = timber;
      post.isPickable = false;
      post.parent = root;
    }
  }

  // The wall plate under the eaves, tying the corner posts together.
  const plate = MeshBuilder.CreateBox(
    `${def.id}-plate`, { width: w + 0.2, depth: d + 0.2, height: 0.28 }, scene
  );
  plate.position.y = def.height - 0.14;
  plate.material = timber;
  plate.isPickable = false;
  plate.parent = root;

  addRoof(scene, root, def, quality);
  addWindows(scene, root, def, quality);
  addDoor(scene, root, def, quality);

  // A chimney on anything with a fire lit inside it.
  if (def.lit === true && def.roof !== 'none') {
    const stack = MeshBuilder.CreateBox(
      `${def.id}-chimney`, { width: 0.8, depth: 0.8, height: Math.min(w, d) * 0.42 + 1.5 }, scene
    );
    stack.position.set(w * 0.3, def.height + (Math.min(w, d) * 0.42 + 1.5) / 2 - 0.2, -d * 0.22);
    stack.material = material(scene, 'mat-stone', 'stone', quality);
    stack.isPickable = false;
    stack.parent = root;
    shadows?.addShadowCaster(stack);
  }

  return root;
}

export function buildProp(
  scene: Scene,
  def: PropDef,
  groundY: number,
  quality: QualitySettings,
  shadows: ShadowGenerator | null
): TransformNode {
  const root = new TransformNode(`prop-${def.id}`, scene);
  root.position.set(def.position[0], groundY, def.position[1]);
  root.rotation.y = def.rotation ?? 0;
  const s = def.scale ?? 1;
  root.scaling.setAll(s);

  const put = (mesh: Mesh, surface: Surface, y: number): Mesh => {
    // Props never push the camera — see the note in camera.ts. A lamp post
    // slamming the camera into the player's back is worse than seeing past it.
    mesh.isPickable = false;
    mesh.position.y = y;
    mesh.material = material(scene, `mat-${surface}`, surface, quality);
    mesh.parent = root;
    shadows?.addShadowCaster(mesh);
    return mesh;
  };

  switch (def.kind) {
    case 'crate':
      put(MeshBuilder.CreateBox(`${def.id}-b`, { size: 0.9 }, scene), 'timber', 0.45);
      break;
    case 'barrel':
      put(MeshBuilder.CreateCylinder(`${def.id}-b`, { diameter: 0.7, height: 1, tessellation: 10 }, scene), 'darkTimber', 0.5);
      break;
    case 'cart': {
      put(MeshBuilder.CreateBox(`${def.id}-bed`, { width: 2.4, depth: 1.3, height: 0.5 }, scene), 'timber', 0.85);
      for (const side of [-1, 1]) {
        const wheel = MeshBuilder.CreateCylinder(`${def.id}-w${side}`, { diameter: 1.1, height: 0.18, tessellation: 12 }, scene);
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(0, 0.55, side * 0.72);
        wheel.material = material(scene, 'mat-darkTimber', 'darkTimber', quality);
        wheel.parent = root;
      }
      break;
    }
    case 'well': {
      put(MeshBuilder.CreateCylinder(`${def.id}-ring`, { diameter: 2, height: 1, tessellation: 14 }, scene), 'stone', 0.5);
      put(MeshBuilder.CreateCylinder(`${def.id}-water`, { diameter: 1.6, height: 0.05, tessellation: 14 }, scene), 'water', 0.85);
      break;
    }
    case 'lamp': {
      put(MeshBuilder.CreateCylinder(`${def.id}-post`, { diameter: 0.16, height: 3.4, tessellation: 8 }, scene), 'metal', 1.7);
      const head = MeshBuilder.CreateBox(`${def.id}-head`, { size: 0.44 }, scene);
      head.position.y = 3.5;
      const glow = new StandardMaterial(`${def.id}-lampglow`, scene);
      glow.emissiveColor = Color3.FromHexString('#ffc36a');
      glow.diffuseColor = Color3.FromHexString('#3a2a18');
      head.material = glow;
      head.parent = root;
      break;
    }
    case 'brazier': {
      put(MeshBuilder.CreateCylinder(`${def.id}-bowl`, { diameterTop: 1.1, diameterBottom: 0.5, height: 0.6, tessellation: 10 }, scene), 'metal', 1);
      const fire = MeshBuilder.CreateSphere(`${def.id}-fire`, { diameter: 0.85, segments: 6 }, scene);
      fire.position.y = 1.35;
      const fireMat = new StandardMaterial(`${def.id}-firemat`, scene);
      fireMat.emissiveColor = Color3.FromHexString('#ff7a2a');
      fireMat.diffuseColor = Color3.FromHexString('#552008');
      fire.material = fireMat;
      fire.parent = root;
      break;
    }
    case 'tree': {
      put(MeshBuilder.CreateCylinder(`${def.id}-trunk`, { diameterTop: 0.4, diameterBottom: 0.7, height: 4.2, tessellation: 8 }, scene), 'darkTimber', 2.1);
      const canopy = MeshBuilder.CreateSphere(`${def.id}-canopy`, { diameter: 4.6, segments: 6 }, scene);
      canopy.position.y = 5.2;
      canopy.scaling.y = 0.78;
      canopy.material = material(scene, 'mat-grass', 'grass', quality);
      canopy.parent = root;
      shadows?.addShadowCaster(canopy);
      break;
    }
    case 'pine': {
      // Three stacked cones. A conifer is the one tree shape that is instantly
      // recognisable in silhouette, which is why it is worth having a second
      // tree at all.
      put(MeshBuilder.CreateCylinder(`${def.id}-trunk`,
        { diameterTop: 0.26, diameterBottom: 0.5, height: 3, tessellation: 6 }, scene),
        'darkTimber', 1.5);
      for (const [i, [y, d, h]] of ([[2.4, 3.6, 2.6], [4, 2.7, 2.3], [5.4, 1.7, 1.9]] as const).entries()) {
        const skirt = MeshBuilder.CreateCylinder(`${def.id}-skirt${i}`,
          { diameterTop: 0, diameterBottom: d, height: h, tessellation: 7 }, scene);
        skirt.position.y = y;
        skirt.material = material(scene, 'mat-pine', 'grass', quality, 2, 0.62);
        skirt.isPickable = false;
        skirt.parent = root;
        if (i === 0) shadows?.addShadowCaster(skirt);
      }
      break;
    }
    case 'deadtree': {
      put(MeshBuilder.CreateCylinder(`${def.id}-trunk`,
        { diameterTop: 0.22, diameterBottom: 0.62, height: 4.6, tessellation: 6 }, scene),
        'darkTimber', 2.3);
      // Two bare limbs, leaning opposite ways. Without them it is a post.
      for (const [i, side] of [-1, 1].entries()) {
        const limb = MeshBuilder.CreateCylinder(`${def.id}-limb${i}`,
          { diameterTop: 0.08, diameterBottom: 0.2, height: 2.1, tessellation: 5 }, scene);
        limb.rotation.z = side * 0.9;
        limb.rotation.y = i * 1.1;
        limb.position.set(side * 0.7, 3.4 + i * 0.5, 0);
        limb.material = material(scene, 'mat-darkTimber', 'darkTimber', quality);
        limb.isPickable = false;
        limb.parent = root;
      }
      break;
    }
    case 'bush': {
      /* Three overlapping lumps at knee height. The job of a bush is not to be
         looked at — it is to put something between the player and the ground so
         the eye has a sense of scale walking through a wood. */
      for (const [i, [x, y, z, d]] of
        ([[0, 0.5, 0, 1.5], [-0.5, 0.38, 0.35, 1.1], [0.45, 0.4, -0.3, 1.2]] as const).entries()) {
        const lump = MeshBuilder.CreateSphere(`${def.id}-l${i}`, { diameter: d, segments: 5 }, scene);
        lump.position.set(x, y, z);
        lump.scaling.y = 0.8;
        lump.material = material(scene, 'mat-grass', 'grass', quality);
        lump.isPickable = false;
        lump.parent = root;
      }
      break;
    }
    case 'stump': {
      put(MeshBuilder.CreateCylinder(`${def.id}-s`,
        { diameterTop: 0.72, diameterBottom: 0.86, height: 0.6, tessellation: 8 }, scene),
        'darkTimber', 0.3);
      // The cut face, a shade lighter — the only part anybody sees from above.
      put(MeshBuilder.CreateCylinder(`${def.id}-cut`,
        { diameter: 0.7, height: 0.06, tessellation: 8 }, scene), 'timber', 0.62);
      break;
    }
    case 'reeds': {
      // A dozen thin blades leaning at different angles, on a seeded pattern so
      // a reed bed does not shimmer differently on every reload.
      for (let i = 0; i < 11; i += 1) {
        const angle = (i * 2.39996) % (Math.PI * 2);
        const radius = 0.15 + (i % 4) * 0.16;
        const blade = MeshBuilder.CreateBox(`${def.id}-r${i}`,
          { width: 0.05, depth: 0.05, height: 1.1 + (i % 3) * 0.35 }, scene);
        blade.position.set(Math.cos(angle) * radius, 0.6, Math.sin(angle) * radius);
        blade.rotation.z = (((i % 5) - 2) / 2) * 0.22;
        blade.rotation.x = (((i % 3) - 1) / 2) * 0.2;
        blade.material = material(scene, 'mat-thatch', 'thatch', quality);
        blade.isPickable = false;
        blade.parent = root;
      }
      break;
    }
    case 'rock': {
      const rock = MeshBuilder.CreatePolyhedron(`${def.id}-r`, { type: 1, size: 1.1 }, scene);
      put(rock, 'darkStone', 0.9);
      break;
    }
    case 'stall': {
      put(MeshBuilder.CreateBox(`${def.id}-top`, { width: 2.6, depth: 1.8, height: 0.14 }, scene), 'timber', 2.3);
      put(MeshBuilder.CreateBox(`${def.id}-counter`, { width: 2.6, depth: 1.6, height: 0.9 }, scene), 'darkTimber', 0.45);
      for (const [dx, dz] of [[-1.2, -0.8], [1.2, -0.8], [-1.2, 0.8], [1.2, 0.8]] as const) {
        const leg = MeshBuilder.CreateCylinder(`${def.id}-leg${dx}${dz}`, { diameter: 0.12, height: 2.3, tessellation: 6 }, scene);
        leg.position.set(dx, 1.15, dz);
        leg.material = material(scene, 'mat-timber', 'timber', quality);
        leg.parent = root;
      }
      break;
    }
    case 'fence': {
      put(MeshBuilder.CreateBox(`${def.id}-rail`, { width: 4, depth: 0.1, height: 0.12 }, scene), 'timber', 1.1);
      put(MeshBuilder.CreateBox(`${def.id}-rail2`, { width: 4, depth: 0.1, height: 0.12 }, scene), 'timber', 0.6);
      for (const dx of [-1.9, 0, 1.9]) {
        const post = MeshBuilder.CreateBox(`${def.id}-post${dx}`, { width: 0.14, depth: 0.14, height: 1.4 }, scene);
        post.position.set(dx, 0.7, 0);
        post.material = material(scene, 'mat-darkTimber', 'darkTimber', quality);
        post.parent = root;
      }
      break;
    }
    case 'anvil': {
      put(MeshBuilder.CreateBox(`${def.id}-block`, { width: 0.7, depth: 0.5, height: 0.6 }, scene), 'darkTimber', 0.3);
      put(MeshBuilder.CreateBox(`${def.id}-anvil`, { width: 1, depth: 0.34, height: 0.32 }, scene), 'metal', 0.76);
      break;
    }
    case 'boat': {
      const hull = MeshBuilder.CreateCylinder(`${def.id}-hull`, { diameter: 1.6, height: 4.4, tessellation: 8 }, scene);
      hull.rotation.x = Math.PI / 2;
      hull.scaling.x = 0.75;
      put(hull, 'darkTimber', 0.4);
      put(MeshBuilder.CreateCylinder(`${def.id}-mast`, { diameter: 0.14, height: 3.4, tessellation: 6 }, scene), 'timber', 2.1);
      break;
    }
    case 'net': {
      const net = MeshBuilder.CreatePlane(`${def.id}-net`, { width: 2.6, height: 2.2 }, scene);
      net.rotation.x = -Math.PI / 2.6;
      put(net, 'thatch', 1.1);
      break;
    }
    case 'signpost': {
      put(MeshBuilder.CreateCylinder(`${def.id}-post`, { diameter: 0.16, height: 2.6, tessellation: 6 }, scene), 'darkTimber', 1.3);
      put(MeshBuilder.CreateBox(`${def.id}-arm`, { width: 1.5, depth: 0.08, height: 0.34 }, scene), 'timber', 2.3);
      break;
    }
    case 'sitesign': {
      // A signboard advertising a BrainO network site — the name is painted onto
      // a canvas texture, the same technique textures.ts uses for surface relief.
      put(MeshBuilder.CreateCylinder(`${def.id}-post`, { diameter: 0.2, height: 3.2, tessellation: 8 }, scene), 'darkTimber', 1.6);
      const board = MeshBuilder.CreateBox(`${def.id}-board`, { width: 3.4, depth: 0.16, height: 1.5 }, scene);
      board.position.y = 3.4;
      const dt = new DynamicTexture(`sign-${def.id}`, { width: 512, height: 232 }, scene, true);
      const c2 = dt.getContext() as unknown as CanvasRenderingContext2D;
      c2.fillStyle = '#efe3c6'; c2.fillRect(0, 0, 512, 232);
      c2.strokeStyle = '#3a2a16'; c2.lineWidth = 12; c2.strokeRect(9, 9, 494, 214);
      c2.fillStyle = '#2a1c0e'; c2.textAlign = 'center'; c2.textBaseline = 'middle';
      const label = String(def.label ?? 'BrainO');
      c2.font = `bold ${label.length > 12 ? 44 : 60}px sans-serif`;
      c2.fillText(label.slice(0, 20), 256, 116);
      dt.update();
      const bmat = new StandardMaterial(`signmat-${def.id}`, scene);
      bmat.diffuseTexture = dt;
      bmat.emissiveColor = new Color3(0.42, 0.38, 0.28);
      bmat.specularColor = new Color3(0.04, 0.04, 0.04);
      board.material = bmat;
      board.isPickable = false;
      board.parent = root;
      shadows?.addShadowCaster(board);
      break;
    }
  }

  return root;
}

/**
 * The display surface of an advertising slot, plus its frame.
 *
 * Returns one node holding both. An earlier version returned only the panel and
 * called setParent(null) on the frame, which left the frame orphaned in the
 * scene — it survived every zone change and quietly accumulated.
 */
export function buildAdSlot(
  scene: Scene, def: AdSlotDef, quality: QualitySettings
): { root: TransformNode; panel: Mesh } {
  const root = new TransformNode(`adslot-${def.id}`, scene);
  root.position.set(def.position[0], def.position[1], def.position[2]);
  root.rotation.y = def.rotation;

  const panel = MeshBuilder.CreatePlane(
    `ad-${def.id}`,
    { width: def.size[0], height: def.size[1] },
    scene
  );
  const mat = new StandardMaterial(`ad-mat-${def.id}`, scene);
  // Until a campaign is booked, a slot shows a neutral board rather than
  // anything that could be mistaken for a real advertisement.
  mat.diffuseColor = Color3.FromHexString('#2f2a26');
  mat.emissiveColor = Color3.FromHexString('#15120f');
  panel.material = mat;
  panel.isPickable = false;
  // Read by the Stage 5 visibility check, which needs the slot id and the
  // distance beyond which a view cannot count.
  panel.metadata = { adSlotId: def.id, viewDistance: def.viewDistance };
  panel.parent = root;

  // A frame, so an empty slot still reads as a deliberate object.
  const frame = MeshBuilder.CreateBox(
    `ad-frame-${def.id}`,
    { width: def.size[0] + 0.3, depth: 0.12, height: def.size[1] + 0.3 },
    scene
  );
  frame.position.z = 0.08;
  frame.material = material(scene, 'mat-darkTimber', 'darkTimber', quality);
  frame.isPickable = false;
  frame.parent = root;

  // The panel comes back too. Stage 5 needs the mesh itself to hang artwork on
  // and to measure against — searching the scene by name for it later would be
  // one rename away from silently drawing no advertisements at all.
  return { root, panel };
}

export function buildTerrain(scene: Scene, zone: ZoneDef, quality: QualitySettings): Mesh[] {
  const meshes: Mesh[] = [];

  const base = MeshBuilder.CreateGround(
    'ground',
    { width: zone.terrain.size[0], height: zone.terrain.size[1], subdivisions: 2 },
    scene
  );
  /* Ground materials get their own cache names because they tile far more
     times than a wall does. Sharing the wall's material here is what would make
     a two-hundred-metre field look like one enormous stone. */
  base.material = material(
    scene, `mat-ground-${zone.terrain.surface}`, zone.terrain.surface, quality, 26
  );
  base.receiveShadows = true;
  // `walkable` is what click-to-move looks for. Buildings are camera blockers
  // too, and without a separate flag clicking a wall would walk you into it.
  base.metadata = { cameraBlocker: true, walkable: true };
  meshes.push(base);

  /* Terraces are RINGS, not discs.
   *
   * The first version drew each one as a solid cylinder of the ring's outer
   * radius. That put a 2.4 m slab across the entire crater floor — an invisible
   * ceiling over the market square that the camera kept colliding with, and
   * which the ground-height maths knew nothing about. A lathe of the ring's
   * cross-section gives the top surface plus the inner and outer walls, and
   * leaves the middle of the crater open, which is the whole point of a crater.
   */
  for (const terrace of zone.terrain.terraces ?? []) {
    if (terrace.height === 0) continue;

    const h = Math.max(0.2, terrace.height);
    const profile = [
      new Vector3(terrace.innerRadius, 0, 0),
      new Vector3(terrace.innerRadius, h, 0),
      new Vector3(terrace.outerRadius, h, 0),
      new Vector3(terrace.outerRadius, 0, 0)
    ];

    const ring = MeshBuilder.CreateLathe(
      `terrace-${terrace.id}`,
      { shape: profile, tessellation: 56, sideOrientation: Mesh.DOUBLESIDE },
      scene
    );
    ring.position.set(terrace.centre[0], 0, terrace.centre[1]);
    ring.material = material(
      scene, `mat-ground-${terrace.surface}`, terrace.surface, quality, 26
    );
    ring.receiveShadows = true;
    ring.metadata = { cameraBlocker: true, walkable: true };
    meshes.push(ring);
  }

  for (const water of zone.water) {
    const plane = MeshBuilder.CreateGround(
      `water-${water.id}`,
      { width: water.size[0], height: water.size[1], subdivisions: 1 },
      scene
    );
    plane.position.set(water.position[0], water.level, water.position[1]);
    plane.material = material(scene, 'mat-water', 'water', quality);
    meshes.push(plane);
  }

  for (const road of zone.roads) {
    for (let i = 0; i < road.points.length - 1; i++) {
      const a = road.points[i];
      const b = road.points[i + 1];
      if (a === undefined || b === undefined) continue;
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const length = Math.hypot(dx, dz);
      if (length < 0.01) continue;

      const segment = MeshBuilder.CreateGround(
        `${road.id}-${i}`,
        { width: road.width, height: length, subdivisions: 1 },
        scene
      );
      segment.position.set((a[0] + b[0]) / 2, 0.02, (a[1] + b[1]) / 2);
      segment.rotation.y = Math.atan2(dx, dz);
      segment.material = material(
        scene, `mat-road-${road.surface}`, road.surface, quality, 6
      );
      segment.receiveShadows = true;
      segment.metadata = { walkable: true };
      meshes.push(segment);
    }
  }

  meshes.push(...groundScatter(scene, zone, quality));
  return meshes;
}

/**
 * Patches of rough ground, scattered across the settled part of the zone.
 *
 * A ground plane is two triangles. However good the texture on it is, the mip
 * chain flattens it to one colour a few metres out, and the eye reads "field of
 * paint" rather than "field". These are real geometry lying just above it —
 * flat, so they never vanish edge-on the way an upright blade of grass does —
 * and they give the ground the thing it has never had: something to look at
 * between here and the buildings.
 *
 * Drawn as thin instances: one draw call for the lot, several hundred patches
 * for the cost of one mesh. That is the only reason this is affordable on a
 * phone, and the reason it is capped by the quality setting.
 */
function groundScatter(scene: Scene, zone: ZoneDef, quality: QualitySettings): Mesh[] {
  const surface = zone.terrain.surface;
  if (surface === 'water') return [];

  // Half as many when the renderer has already decided this device is
  // struggling; none at all would leave the ground visibly barer on a phone
  // than on a desktop, which is worse than fewer.
  const count = quality.shadows ? 900 : 400;
  const reach = Math.min(zone.terrain.size[0], zone.terrain.size[1]) * 0.42;

  // Same seeded idea as the textures: the field looks the same on every visit.
  let state = 20260730;
  const rand = (): number => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };

  const made: Mesh[] = [];

  /* Two batches, a shade either side of the ground colour. One batch alone
     reads as a rash of identical spots; two read as uneven ground. */
  /* Both shades sit *below* the ground colour, not either side of it. The
     ground carries a texture that darkens it by roughly a tenth on average, so
     a patch at the nominal colour already reads as lighter than the grass it is
     lying on. */
  for (const [index, tint] of [[0, 0.99], [1, 0.87]] as const) {
    /* A seven-sided disc, not a square.
       Square patches came out looking like rugs laid on a lawn — the eye finds
       a straight edge on the ground immediately. Seven sides is few enough to
       stay cheap and odd enough that no two rotations look like the same shape.
       Double-sided so a patch on a slope is never culled from below. */
    const patch = MeshBuilder.CreateDisc(
      `scatter-${index}`,
      { radius: 0.5, tessellation: 7, sideOrientation: Mesh.DOUBLESIDE },
      scene
    );
    const mat = new StandardMaterial(`scatter-mat-${index}`, scene);
    mat.diffuseColor = Color3.FromHexString(SURFACE_COLOURS[surface] ?? '#8c8378').scale(tint);
    mat.specularColor = new Color3(0.02, 0.02, 0.02);
    /* No texture on these, deliberately. One tile of it stretched across a
       four-metre patch came out as diagonal streaks — and the job here is only
       to break up a flat colour, which a flat colour a few per cent either side
       of the ground does perfectly well. */
    patch.material = mat;
    patch.isPickable = false;
    /* Receiving shadow is the whole difference between "uneven ground" and
       "lily pads": without it a patch stays fully lit while the ground it sits
       on is in a building's shadow, and the join is a hard bright edge. */
    patch.receiveShadows = true;

    const matrices = new Float32Array(16 * count);
    for (let i = 0; i < count; i += 1) {
      const angle = rand() * Math.PI * 2;
      // sqrt keeps the density even rather than crowding the middle.
      const distance = Math.sqrt(rand()) * reach;
      const x = Math.cos(angle) * distance;
      const z = Math.sin(angle) * distance;
      const scale = 0.8 + rand() * 1.9;

      Matrix.Compose(
        new Vector3(scale, scale * (0.7 + rand() * 0.6), 1),
        // Pitched flat: the disc is built standing up in the XY plane, and the
        // base mesh's own rotation is ignored once thin instances take over.
        Quaternion.RotationYawPitchRoll(rand() * Math.PI * 2, -Math.PI / 2, 0),
        // 4 cm up. Level with the ground it would z-fight; any higher and the
        // patch casts a visible lip where it meets the grass.
        new Vector3(x, groundHeightAt(x, z, zone) + 0.04, z)
      ).copyToArray(matrices, i * 16);
    }

    patch.thinInstanceSetBuffer('matrix', matrices, 16);
    patch.thinInstanceCount = count;
    made.push(patch);
  }

  return made;
}
